import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, FileText, Users, BarChart, ChevronRight, Folder, Info, ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase, fromTable } from '@/integrations/supabase/client';

type Project = {
  id: string;
  name: string;
  description: string;
  category: string;
  created_at: string;
  user_id: string;
  feedbacksCount: number;
  requestsCount: number;
};

type ProjectStats = {
  id: string;
  strengths: Record<string, number>;
  improvements: Record<string, number>;
  ratings: number[];
  totalFeedbacks: number;
};

type ProjectFeedback = {
  id: string;
  feedback_id: string;
  project_id: string;
  feedbackDetails: {
    id: string;
    candidate_name: string;
    job_title: string;
    created_at: string;
  } | null;
};

type ProjectRequest = {
  id: string;
  request_id: string;
  project_id: string;
  requestDetails: {
    id: string;
    candidate_name: string;
    job_title: string;
    status: string;
    created_at: string;
  } | null;
};

const ProjectsTab = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectStats, setProjectStats] = useState<ProjectStats | null>(null);
  const [projectFeedbacks, setProjectFeedbacks] = useState<ProjectFeedback[]>([]);
  const [projectRequests, setProjectRequests] = useState<ProjectRequest[]>([]);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', category: 'recrutement' });
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const generateProjectStats = (projectId: string): ProjectStats => {
    return {
      id: projectId,
      strengths: {
        'Communication': Math.floor(Math.random() * 5) + 1,
        'Compétences techniques': Math.floor(Math.random() * 5) + 1,
        'Résolution de problèmes': Math.floor(Math.random() * 5) + 1,
        'Travail d\'équipe': Math.floor(Math.random() * 5) + 1
      },
      improvements: {
        'Leadership': Math.floor(Math.random() * 3) + 1,
        'Gestion du temps': Math.floor(Math.random() * 3) + 1,
        'Documentation': Math.floor(Math.random() * 3) + 1
      },
      ratings: [3, 4, 5, 4, 5],
      totalFeedbacks: Math.floor(Math.random() * 5) + 1
    };
  };

  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user]);

  const fetchProjects = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data: projectsData, error } = await fromTable('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      if (projectsData) {
        const projectsWithCounts = await Promise.all(projectsData.map(async (project) => {
          const { count: feedbacksCount, error: feedbackCountError } = await fromTable('projects_feedbacks')
            .select('*', { count: 'exact', head: true })
            .eq('project_id', project.id)
            .eq('user_id', user.id);
          
          if (feedbackCountError) throw feedbackCountError;
          
          const { count: requestsCount, error: requestCountError } = await fromTable('projects_requests')
            .select('*', { count: 'exact', head: true })
            .eq('project_id', project.id)
            .eq('user_id', user.id);
          
          if (requestCountError) throw requestCountError;
          
          return {
            ...project,
            feedbacksCount: feedbacksCount || 0,
            requestsCount: requestsCount || 0
          } as Project;
        }));
        
        setProjects(projectsWithCounts);
      }
    } catch (error: any) {
      console.error('Erreur lors de la récupération des projets:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger vos projets',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (project: Project) => {
    setSelectedProject(project);
    setDetailsDialogOpen(true);

    try {
      const { data: feedbacksData, error: feedbacksError } = await fromTable('projects_feedbacks')
        .select(`
          id,
          feedback_id,
          project_id,
          feedbackDetails:feedbacks(id, candidate_name, job_title, created_at)
        `)
        .eq('project_id', project.id)
        .eq('user_id', user?.id);
      
      if (feedbacksError) throw feedbacksError;
      
      const typedFeedbacksData = (feedbacksData || []).map(item => {
        return {
          id: item.id,
          feedback_id: item.feedback_id,
          project_id: item.project_id,
          feedbackDetails: item.feedbackDetails?.[0] || null
        } as ProjectFeedback;
      });
      
      setProjectFeedbacks(typedFeedbacksData);

      const { data: requestsData, error: requestsError } = await fromTable('projects_requests')
        .select(`
          id,
          request_id,
          project_id,
          requestDetails:candidate_to_recruiter_feedback_requests(id, candidate_name, job_title, status, created_at)
        `)
        .eq('project_id', project.id)
        .eq('user_id', user?.id);
      
      if (requestsError) throw requestsError;
      
      const typedRequestsData = (requestsData || []).map(item => {
        return {
          id: item.id,
          request_id: item.request_id,
          project_id: item.project_id,
          requestDetails: item.requestDetails?.[0] || null
        } as ProjectRequest;
      });
      
      setProjectRequests(typedRequestsData);

    } catch (error: any) {
      console.error('Erreur lors de la récupération des détails du projet:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les détails du projet',
        variant: 'destructive'
      });
    }
  };

  const handleViewProject = (project: Project) => {
    fetchProjectDetails(project);
  };

  const handleViewAnalysis = (project: Project) => {
    setSelectedProject(project);
    setProjectStats(generateProjectStats(project.id));
    setStatsDialogOpen(true);
  };

  const handleCreateProject = () => {
    setCreateDialogOpen(true);
  };

  const handleSaveProject = async () => {
    if (!user) return;
    
    if (!newProject.name.trim()) {
      toast({
        title: 'Erreur',
        description: 'Le nom du projet est requis',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const { data, error } = await fromTable('projects')
        .insert({
          name: newProject.name,
          description: newProject.description,
          category: newProject.category,
          user_id: user.id
        })
        .select();
      
      if (error) throw error;
      
      toast({
        title: 'Succès',
        description: 'Projet créé avec succès',
      });
      
      setCreateDialogOpen(false);
      setNewProject({ name: '', description: '', category: 'recrutement' });
      
      if (data) {
        const newProjectWithCounts = {
          ...data[0],
          feedbacksCount: 0,
          requestsCount: 0
        } as Project;
        
        setProjects([...projects, newProjectWithCounts]);
      } else {
        fetchProjects();
      }
    } catch (error: any) {
      console.error('Erreur lors de la création du projet:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de créer le projet',
        variant: 'destructive'
      });
    }
  };

  const handleViewFeedback = (feedbackId: string) => {
    navigate(`/view-feedback/${feedbackId}`);
  };

  const handleViewRequest = (requestId: string) => {
    navigate(`/feedback/${requestId}`);
  };

  const getCategoryLabel = (category: string) => {
    switch(category) {
      case 'recrutement': return 'Recrutement';
      case 'candidature': return 'Candidature';
      case 'mission': return 'Mission';
      default: return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  const getCategoryColor = (category: string) => {
    switch(category) {
      case 'recrutement': return 'bg-blue-100 text-blue-800';
      case 'candidature': return 'bg-purple-100 text-purple-800';
      case 'mission': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Mes Projets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Mes Projets</CardTitle>
        <Button size="sm" className="flex items-center gap-1" onClick={handleCreateProject}>
          <Plus className="h-4 w-4" />
          Nouveau projet
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div key={project.id} className="border rounded-lg hover:shadow-md transition overflow-hidden">
              <div className={`p-3 text-white ${project.category === 'recrutement' ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 
                               project.category === 'candidature' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                               'bg-gradient-to-r from-green-500 to-green-600'}`}>
                <h3 className="font-semibold text-lg">{project.name}</h3>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <FileText className="h-4 w-4" />
                  <span>{project.feedbacksCount} feedbacks</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Users className="h-4 w-4" />
                  <span>{project.requestsCount} demandes</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  <Badge className={getCategoryColor(project.category)}>
                    {getCategoryLabel(project.category)}
                  </Badge>
                </div>
                <div className="flex justify-between mt-4">
                  <Button variant="ghost" size="sm" className="flex items-center gap-1" onClick={() => handleViewAnalysis(project)}>
                    <BarChart className="h-4 w-4" />
                    <span>Analyse</span>
                  </Button>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1" onClick={() => handleViewProject(project)}>
                    <span>Voir</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          
          <div className="border border-dashed rounded-lg hover:shadow-md transition cursor-pointer p-6 flex flex-col items-center justify-center text-gray-500 hover:text-gray-700"
               onClick={handleCreateProject}>
            <Plus className="h-8 w-8 mb-2" />
            <p>Créer un nouveau projet</p>
          </div>
        </div>
      </CardContent>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un nouveau projet</DialogTitle>
            <DialogDescription>
              Organisez vos feedbacks et demandes en projets
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Nom du projet</Label>
              <Input 
                id="project-name" 
                value={newProject.name}
                onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                placeholder="Ex: Recrutement Développeurs Frontend"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea 
                id="project-description"
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                placeholder="Description du projet (optionnelle)"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-category">Catégorie</Label>
              <Select
                value={newProject.category}
                onValueChange={(value) => setNewProject({...newProject, category: value})}
              >
                <SelectTrigger id="project-category">
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recrutement">Recrutement</SelectItem>
                  <SelectItem value="candidature">Candidature</SelectItem>
                  <SelectItem value="mission">Mission</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveProject}>Créer le projet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5" />
              {selectedProject?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedProject?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <h3 className="font-semibold mb-2">Feedbacks et demandes associés</h3>
            
            {selectedProject && ((projectFeedbacks && projectFeedbacks.length > 0) || (projectRequests && projectRequests.length > 0)) ? (
              <div className="space-y-4">
                {projectFeedbacks && projectFeedbacks.length > 0 && (
                  <div className="border rounded-md p-4">
                    <h4 className="font-medium mb-2">Feedbacks ({projectFeedbacks.length})</h4>
                    <ul className="space-y-2">
                      {projectFeedbacks.map((item) => (
                        item.feedbackDetails && (
                          <li key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <div>
                              <span className="font-medium">{item.feedbackDetails.candidate_name}</span>
                              <p className="text-sm text-gray-500">{item.feedbackDetails.job_title}</p>
                              <p className="text-xs text-gray-400">
                                {new Date(item.feedbackDetails.created_at).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewFeedback(item.feedback_id)}
                            >
                              Voir
                            </Button>
                          </li>
                        )
                      ))}
                    </ul>
                  </div>
                )}
                
                {projectRequests && projectRequests.length > 0 && (
                  <div className="border rounded-md p-4">
                    <h4 className="font-medium mb-2">Demandes ({projectRequests.length})</h4>
                    <ul className="space-y-2">
                      {projectRequests.map((item) => (
                        item.requestDetails && (
                          <li key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <div>
                              <span className="font-medium">{item.requestDetails.candidate_name}</span>
                              <p className="text-sm text-gray-500">{item.requestDetails.job_title}</p>
                              <div className="flex items-center gap-1">
                                <Badge variant={item.requestDetails.status === 'completed' ? 'secondary' : 
                                               item.requestDetails.status === 'pending' ? 'outline' : 'default'}>
                                  {item.requestDetails.status === 'completed' ? 'Complété' : 
                                   item.requestDetails.status === 'pending' ? 'En attente' : 
                                   item.requestDetails.status === 'cancelled' ? 'Annulé' : 'Expiré'}
                                </Badge>
                                <span className="text-xs text-gray-400">
                                  {new Date(item.requestDetails.created_at).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewRequest(item.request_id)}
                            >
                              Voir
                            </Button>
                          </li>
                        )
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Info className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Aucun feedback ou demande associé à ce projet</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>Fermer</Button>
            <Button onClick={() => handleViewAnalysis(selectedProject!)}>Voir l'analyse</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart className="h-5 w-5" />
              Analyse: {selectedProject?.name}
            </DialogTitle>
            <DialogDescription>
              Analyse des feedbacks reçus dans ce projet
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {projectStats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-md p-4">
                    <h3 className="font-semibold mb-3 text-green-600">Points forts mentionnés</h3>
                    <ul className="space-y-2">
                      {Object.entries(projectStats.strengths).map(([skill, count]) => (
                        <li key={skill} className="flex justify-between">
                          <span>{skill}</span>
                          <div className="flex gap-1">
                            {[...Array(count)].map((_, i) => (
                              <span key={i} className="h-4 w-4 bg-green-200 rounded-full"></span>
                            ))}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="border rounded-md p-4">
                    <h3 className="font-semibold mb-3 text-orange-600">Axes d'amélioration</h3>
                    <ul className="space-y-2">
                      {Object.entries(projectStats.improvements).map(([skill, count]) => (
                        <li key={skill} className="flex justify-between">
                          <span>{skill}</span>
                          <div className="flex gap-1">
                            {[...Array(count)].map((_, i) => (
                              <span key={i} className="h-4 w-4 bg-orange-200 rounded-full"></span>
                            ))}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="border rounded-md p-4">
                  <h3 className="font-semibold mb-3">Notes globales</h3>
                  <div className="flex items-end justify-center gap-2 py-2 h-24">
                    {projectStats.ratings.map((rating, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div className="bg-blue-400 rounded-t-md" style={{height: `${rating * 6}px`, width: '24px'}}></div>
                        <span className="text-xs mt-1">{rating}/5</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-2 text-sm text-gray-600">
                    Note moyenne: {(projectStats.ratings.reduce((acc, val) => acc + val, 0) / projectStats.ratings.length).toFixed(1)}/5
                  </div>
                </div>
                
                <div className="text-center text-sm text-gray-500">
                  Analyse basée sur {projectStats.totalFeedbacks} feedbacks
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Info className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Pas assez de données pour générer une analyse</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStatsDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProjectsTab;
